import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { selectPatchTransactionStatus, selectTransaction } from 'store/transactions/selectors';
import { useLocale } from 'hooks/use-locale.hook';
import { selectBuildingTimeZone } from 'store/building/selectors';
import { formatDate } from 'utils';
import { getTransaction, patchTransaction } from 'store/transactions/actions';
import { ACTION_STATUSES, PATCH_STATUSES, TRACK_EVENTS, TRACK_EVENT_TYPES } from 'shared/consts';
import { useLocation } from 'react-router';
import { useSearchParams } from 'hooks/use-search-params.hook';
import qs from 'qs';
import { replace } from 'connected-react-router';
import { track } from '@hqo/web-tracking';

interface UseCancelBookingReturnValues {
  eventWeekDay: string;
  eventDateMonthLong: string;
  eventDateDay: string;
  formatEventStartTime: string;
  vendorName: string;
  onCancelButtonClick: VoidFunction;
}

// eslint-disable-next-line max-lines-per-function
export const useCancelBooking = (): UseCancelBookingReturnValues => {
  const locale = useLocale();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const buildingTimeZone = useSelector(selectBuildingTimeZone);
  const transaction = useSelector(selectTransaction);
  const patchTransactionStatus = useSelector(selectPatchTransactionStatus);
  const { confirmCancel, ...restQueryParams } = useSearchParams();
  const queryString = qs.stringify({ ...restQueryParams });

  const { id, uuid, details } = transaction || {};
  const vendorName = details?.service?.name;

  const formattedDate = formatDate(details?.start_at, locale, buildingTimeZone);
  const {
    eventWeekDayLong,
    eventDateMonthLong,
    eventDateDay,
    formatEventTime: formatEventStartTime,
  } = formattedDate || {};

  useEffect(() => {
    if (patchTransactionStatus === ACTION_STATUSES.FULFILLED) {
      dispatch(getTransaction.request(uuid));
      dispatch(replace(`${pathname}?${queryString}`));
    }
  }, [dispatch, patchTransactionStatus, pathname, uuid, queryString, confirmCancel]);

  const onCancelButtonClick = useCallback(() => {
    track(
      TRACK_EVENTS.SB_CANCEL_CONFIRMATION_CLICK,
      {
        type: TRACK_EVENT_TYPES.ACTION,
      },
      { sendToHqoTracking: true },
    );

    dispatch(
      patchTransaction.request({
        transactionId: id?.toString(),
        status: PATCH_STATUSES.CANCELLED,
      }),
    );
  }, [dispatch, id]);

  return {
    eventWeekDay: eventWeekDayLong,
    eventDateMonthLong,
    eventDateDay,
    formatEventStartTime,
    vendorName,
    onCancelButtonClick,
  };
};
